"use client";

import { motion } from "framer-motion";
import { MessageSquare, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
import ExcursionNotice from "@/components/ExcursionNotice";
import ExcursionTerms from "@/components/ExcursionTerms";
import { excursionTerms, getExcursionWhatsAppUrl, getCairoWhatsAppUrl, getLuxorWhatsAppUrl } from "@/lib/excursionTerms";
import { excursionDetailHref } from "@/lib/sitePages";
interface ExcursionItem {
  id: string;
  title: string;
  description: string;
  detailSlug: string;
  image: string;
  srcset: string;
  sizes: string;
  alt: string;
}
interface ExcursionCategory {
  id: string;
  title: string;
  subtitle: string;
  items: ExcursionItem[];
}
const excursionCategories: ExcursionCategory[] = [{
  id: "sea",
  title: "Sea & Island Experiences",
  subtitle: "Island trips, semi-submarine viewing, diving, and coastal water activities on the Red Sea",
  items: [{
    id: "trip-orange-bay",
    title: "Orange Bay Island Trip",
    description: "Enjoy a Red Sea boat trip with beach relaxation on Orange Bay and guided snorkeling stops along the reef. Programme timing, meals and snorkeling equipment are confirmed in your quotation.",
    detailSlug: "orange-bay-island-trip",
    image: "https://devoted-mite.10web.cloud/wp-content/uploads/2026/09/Orang.webp?v=dp0726bles9",
    srcset: "https://devoted-mite.10web.cloud/wp-content/uploads/2026/09/Orange-Bay-Island.webp?v=zkj5klku5z8-wvc-srcset",
    sizes: "https://devoted-mite.10web.cloud/wp-content/uploads/2026/09/Orange-Bay-Island.webp?v=6fo42qnq4rb-wvc-sizes",
    alt: "Orange Bay Island Trip sandy beach and turquoise waters"
  }, {
    id: "trip-paradise-island",
    title: "Paradise Island Trip",
    description: "Combine island relaxation on Paradise Island with boat cruising and open-water snorkeling stops. Island stay duration, lunch arrangements and gear are confirmed in your quotation.",
    detailSlug: "paradise-island-trip",
    image: "https://hurghadatransferhub.com/wp-content/uploads/2026/10/Paradise-Island-.webp?v=pbj7rc2pebg",
    srcset: "https://hurghadatransferhub.com/wp-content/uploads/2026/10/Paradise-Island-.webp?v=dmunzyvmlm-wvc-srcset",
    sizes: "https://hurghadatransferhub.com/wp-content/uploads/2026/10/Paradise-Island-.webp?v=y0mito01r8b-wvc-sizes",
    alt: "Paradise Island Trip sandy shoreline and Red Sea waters"
  }, {
    id: "trip-private-speedboat",
    title: "Private Speedboat Trip",
    description: "Charter a private speedboat reserved exclusively for you and your party. Customisable by destination, duration and group requirements with itinerary, stops and price confirmed in your quotation.",
    detailSlug: "private-speedboat-trip",
    image: "https://hurghadatransferhub.com/wp-content/uploads/2026/10/Speed.jpeg?v=zgjkspbd8e9",
    srcset: "https://hurghadatransferhub.com/wp-content/uploads/2026/10/Speed.jpeg?v=zgjkspbd8e9-wvc-srcset",
    sizes: "https://hurghadatransferhub.com/wp-content/uploads/2026/10/Speed.jpeg?v=zgjkspbd8e9-wvc-sizes",
    alt: "Private Speedboat Trip charter reserved exclusively for your party on the Red Sea"
  }, {
    id: "trip-submarine",
    title: "Sea Scope Semi-Submarine",
    description: "View Red Sea coral reefs and marine life through underwater panoramic observation windows while the vessel stays safely on the surface. Additional snorkeling stops are confirmed in your quotation.",
    detailSlug: "semi-submarine-red-sea-trip",
    image: "https://devoted-mite.10web.cloud/wp-content/uploads/2026/09/80.jpg?v=s0fv2hn7a8",
    srcset: "https://devoted-mite.10web.cloud/wp-content/uploads/2026/09/80.jpg?v=komg2bv40z-wvc-srcset",
    sizes: "https://devoted-mite.10web.cloud/wp-content/uploads/2026/09/80.jpg?v=talg2wbvnbe-wvc-sizes",
    alt: "Sea Scope Semi-Submarine underwater observation windows showing coral reef"
  }, {
    id: "trip-diving-boat",
    title: "Diving Boat Trip",
    description: "Explore Red Sea dive sites on a guided boat excursion. Certified divers and introductory trial dives are accommodated, with dive count, depths, equipment and supervision confirmed in your quotation.",
    detailSlug: "diving-boat-trip",
    image: "https://hurghadatransferhub.com/wp-content/uploads/2026/10/IMG_8675-scaled.jpeg?v=7fhjt3umewr",
    srcset: "https://hurghadatransferhub.com/wp-content/uploads/2026/10/IMG_9002-scaled.jpeg?v=3m8z2age9u7-wvc-srcset",
    sizes: "https://hurghadatransferhub.com/wp-content/uploads/2026/10/IMG_9002-scaled.jpeg?v=hfm1tpaxyrf-wvc-sizes",
    alt: "Diving Boat Trip in the Red Sea from Hurghada"
  }, {
    id: "trip-parasailing",
    title: "Parasailing",
    description: "Ascend over the Red Sea coastline with single or tandem parasailing options, high-standard safety equipment and boat briefing. Flight duration and flight combinations are confirmed in your quotation.",
    detailSlug: "parasailing",
    image: "https://hurghadatransferhub.com/wp-content/uploads/2026/10/IMG_8821-scaled.jpeg?v=yyd6njb3fu",
    srcset: "https://hurghadatransferhub.com/wp-content/uploads/2026/10/IMG_8814-scaled.jpeg?v=l3cm4jwus99-wvc-srcset",
    sizes: "https://hurghadatransferhub.com/wp-content/uploads/2026/10/IMG_8814-scaled.jpeg?v=2mizqptriyo-wvc-sizes",
    alt: "Parasailing flight over the Red Sea coastline"
  }]
}, {
  id: "desert",
  title: "Desert & Horse Riding Adventures",
  subtitle: "Quad biking, buggy safaris, desert dunes, and guided horse riding adventures",
  items: [{
    id: "trip-desert-safari",
    title: "Super Safari Adventure",
    description: "Experience the Eastern Desert with varied off-road safari programmes. Driving segments, buggy or quad trails, Bedouin camp stops, dinner and entertainment are confirmed in your quotation.",
    detailSlug: "super-safari-adventure",
    image: "https://hurghadatransferhub.com/wp-content/uploads/2026/10/IMG_8983-scaled.jpeg?v=zu3k34zbl",
    srcset: "https://devoted-mite.10web.cloud/wp-content/uploads/2026/09/Super-Safari.webp?v=xkp2m0lnw8n-wvc-srcset",
    sizes: "https://devoted-mite.10web.cloud/wp-content/uploads/2026/09/Super-Safari.webp?v=cqq31c7bmfo-wvc-sizes",
    alt: "Super Safari Adventure across Hurghada desert terrain"
  }, {
    id: "trip-buggy-safari",
    title: "Buggy Safari",
    description: "Navigate desert trails behind the wheel of a rugged dune buggy. Route selection, driving duration, vehicle seating capacity and trail stops are confirmed in your quotation.",
    detailSlug: "buggy-safari",
    image: "https://hurghadatransferhub.com/wp-content/uploads/2026/10/pexels-koto-28932608-636x426-1.webp?v=8bf79mx80ze",
    srcset: "https://hurghadatransferhub.com/wp-content/uploads/2026/10/IMG_8983-scaled.jpeg?v=zgoo34xxwa-wvc-srcset",
    sizes: "https://hurghadatransferhub.com/wp-content/uploads/2026/10/IMG_8983-scaled.jpeg?v=w0yv8tri9ka-wvc-sizes",
    alt: "Buggy Safari navigating across Egyptian desert terrain"
  }, {
    id: "trip-quad-bike-safari",
    title: "Quad Bike Safari",
    description: "Ride all-terrain four-wheel quad bikes along desert trails with instructor guidance and safety briefing. Exact route distance, riding duration and schedule are confirmed in your quotation.",
    detailSlug: "quad-bike-safari",
    image: "https://hurghadatransferhub.com/wp-content/uploads/2026/10/DSC_0015.JPG-scaled.jpeg?v=0oj9jgfobok",
    srcset: "https://hurghadatransferhub.com/wp-content/uploads/2026/09/Super-Safari.webp?v=1n9ioyvne43-wvc-srcset",
    sizes: "https://hurghadatransferhub.com/wp-content/uploads/2026/09/Super-Safari.webp?v=0qrkelxxwpgf-wvc-sizes",
    alt: "Quad Bike Safari along scenic Red Sea desert trails"
  }, {
    id: "trip-horse-riding",
    title: "Horse Riding Trip",
    description: "Guided horse riding trail through desert and coastal terrain accompanied by experienced handlers. Ride route, pace and participation requirements are confirmed in your quotation.",
    detailSlug: "horse-riding-trip",
    image: "https://hurghadatransferhub.com/wp-content/uploads/2026/10/IMG_9002-scaled.jpeg?v=ojai42cyr6",
    srcset: "https://hurghadatransferhub.com/wp-content/uploads/2026/10/IMG_9002-scaled.jpeg?v=s3zr2by9iid-wvc-srcset",
    sizes: "https://hurghadatransferhub.com/wp-content/uploads/2026/10/IMG_9002-scaled.jpeg?v=rdmyk6sta1-wvc-sizes",
    alt: "Horse Riding Trip through desert and coastal plains in Hurghada"
  }, {
    id: "trip-sea-mountain-horse-riding",
    title: "Sea & Mountain Horse Riding",
    description: "Scenic trail ride crossing between coastal shoreline areas and desert ridge landscapes. The exact trail route, duration and riding requirements are confirmed in your quotation.",
    detailSlug: "sea-mountain-horse-riding",
    image: "https://hurghadatransferhub.com/wp-content/uploads/2026/10/e3.jpg?v=b5r8hlb1fra",
    srcset: "https://hurghadatransferhub.com/wp-content/uploads/2026/10/e3.jpg?v=3od3k5h6chn-wvc-srcset",
    sizes: "https://hurghadatransferhub.com/wp-content/uploads/2026/10/e3.jpg?v=fv94iugxysa-wvc-sizes",
    alt: "Sea & Mountain Horse Riding trail route in Hurghada"
  }]
}, {
  id: "culture",
  title: "Culture & Sightseeing",
  subtitle: "Historic day trips to Cairo and Luxor, authentic city highlights, and landmark parks",
  items: [{
    id: "trip-cairo",
    title: "Private Cairo Tour from Hurghada",
    description: "Fully private day tour with private return road transport and private guide exclusively for your party. Standard admission to Giza Pyramids, Great Sphinx, Grand Egyptian Museum (GEM) and lunch included.",
    detailSlug: "cairo-pyramids-day-tour",
    image: "https://devoted-mite.10web.cloud/wp-content/uploads/2026/09/cairo_luxor_tours.webp?v=81t66ojr0oo",
    srcset: "https://devoted-mite.10web.cloud/wp-content/uploads/2026/09/Untitled-design-1-1024x573-1.webp?v=hcy5gugxkuk-wvc-srcset",
    sizes: "https://devoted-mite.10web.cloud/wp-content/uploads/2026/09/Untitled-design-1-1024x573-1.webp?v=gjoud16ugm-wvc-sizes",
    alt: "Private Cairo Tour from Hurghada with Giza Pyramids and Sphinx"
  }, {
    id: "trip-luxor",
    title: "Private Luxor Tour from Hurghada",
    description: "Fully private day tour with private return road transport and private guide exclusively for your party. Standard admission to Karnak Temple, Valley of the Kings (three tombs), Temple of Hatshepsut, Colossi of Memnon and lunch included.",
    detailSlug: "luxor-day-tour",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1810038/img/luxor_temple_excursion.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1810038/img/luxor_temple_excursion.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1810038/img/luxor_temple_excursion.png-wvc-sizes",
    alt: "Private Luxor Tour from Hurghada showing Karnak Temple monuments"
  }, {
    id: "trip-hurghada-city-tour",
    title: "Hurghada Highlights City Tour",
    description: "Explore Hurghada landmarks including the El Mina Mosque exterior, marina promenade and local viewpoints. Specific site access, stops and tour duration are confirmed in your quotation.",
    detailSlug: "hurghada-highlights-city-tour",
    image: "https://hurghadatransferhub.com/wp-content/uploads/2026/10/Hurghada-Highlights-City-Tour-1.webp?v=ba1yli3sf9b",
    srcset: "https://hurghadatransferhub.com/wp-content/uploads/2026/10/Hurghada-Highlights-City-Tour-1.webp?v=laykqpo3k9d-wvc-srcset",
    sizes: "https://hurghadatransferhub.com/wp-content/uploads/2026/10/Hurghada-Highlights-City-Tour-1.webp?v=22ajr55n0zmj-wvc-sizes",
    alt: "Hurghada Highlights City Tour showing the marina and city views"
  }, {
    id: "trip-mini-egypt-park",
    title: "Mini Egypt Park Tour",
    description: "Discover detailed scale miniature models of Egypt's most celebrated historical landmarks in an open-air setting. Visit duration and ticket options are confirmed in your quotation.",
    detailSlug: "mini-egypt-park-tour",
    image: "https://hurghadatransferhub.com/wp-content/uploads/2026/10/images.jpeg?v=ctmova737ns",
    srcset: "https://hurghadatransferhub.com/wp-content/uploads/2026/10/cairo_pyramids_excursion.png?v=0fmbhq4wlcn-wvc-srcset",
    sizes: "https://hurghadatransferhub.com/wp-content/uploads/2026/10/cairo_pyramids_excursion.png?v=vrgxb90zp9a-wvc-sizes",
    alt: "Mini Egypt Park Tour miniature architectural models"
  }]
}, {
  id: "shows",
  title: "Shows & Experiences",
  subtitle: "Family-friendly entertainment and marine experiences with direct hotel transfers",
  items: [{
    id: "trip-dolphin-world-show",
    title: "Dolphin Show",
    description: "Attend an entertaining marine mammal presentation with family-friendly seating. Show times and seat categories are confirmed in your quotation. Swimming and photography are booked separately.",
    detailSlug: "dolphin-world-show",
    image: "https://hurghadatransferhub.com/wp-content/uploads/2026/10/unnamed-1.webp?v=gf5nk3i762m",
    srcset: "https://hurghadatransferhub.com/wp-content/uploads/2026/10/unnamed-1.webp?v=n5idfhbmu7-wvc-srcset",
    sizes: "https://hurghadatransferhub.com/wp-content/uploads/2026/10/unnamed-1.webp?v=sehyktev1wg-wvc-sizes",
    alt: "Dolphin Show marine animal presentation"
  }, {
    id: "trip-dolphin-world-swimming",
    title: "Swimming with Dolphins at Dolphin World",
    description: "Participate in a managed pool-based swimming experience with dolphins at Dolphin World. Session duration and participant guidelines are confirmed in advance; show access and photos are separate.",
    detailSlug: "swimming-with-dolphins-at-dolphin-world",
    image: "https://hurghadatransferhub.com/wp-content/uploads/2026/10/unnamed-2.webp?v=hyqpz2h2abv",
    srcset: "https://hurghadatransferhub.com/wp-content/uploads/2026/10/unnamed-1.webp?v=n5idfhbmu7-wvc-srcset",
    sizes: "https://hurghadatransferhub.com/wp-content/uploads/2026/10/unnamed-1.webp?v=sehyktev1wg-wvc-sizes",
    alt: "Swimming with Dolphins at Dolphin World managed pool experience"
  }]
}];
const containerVariants = {
  hidden: {},
  show: {
    transition: {
      staggerChildren: 0.05
    }
  }
};
const cardVariants = {
  hidden: {
    opacity: 0,
    y: 18
  },
  show: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.45,
      ease: "easeOut"
    }
  }
};
export default function Excursions() {
  const buildWhatsappLink = (trip: ExcursionItem) => {
    if (trip.id === "trip-cairo") {
      return getCairoWhatsAppUrl("en");
    }
    if (trip.id === "trip-luxor") {
      return getLuxorWhatsAppUrl("en");
    }
    return getExcursionWhatsAppUrl("en", trip.title);
  };
  return <section id="day-trip-excursions" data-nav="light" className="relative bg-background text-foreground py-16 md:py-20 border-b border-border">
      <div className="mx-auto max-w-[1136px] px-6">
        {/* Header Block */}
        <motion.div initial={{
        opacity: 0,
        y: 16
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.5,
        ease: "easeOut"
      }} className="flex flex-col items-center text-center max-w-2xl mx-auto mb-14 md:mb-16">
          <span className="text-xs uppercase tracking-wider font-semibold text-primary mb-3">
            Excursions & Day Tours Catalogue
          </span>
          <h2 className="text-3xl md:text-4xl lg:text-[2.6rem] font-bold tracking-tight text-foreground leading-tight">
            Day Trip Excursions From Hurghada
          </h2>

          {/* Signature Kit Device: Hairline rule capped with primary terminal dot */}
          <div className="flex items-center justify-center my-4 w-32" aria-hidden="true">
            <div className="h-[1px] bg-border flex-1" />
            <div className="size-2 rounded-lg bg-primary mx-1" />
            <div className="h-[1px] bg-border flex-1" />
          </div>

          <p className="text-base md:text-lg text-muted-foreground leading-relaxed">
            Discover our approved catalogue of 17 Red Sea excursions and day tours. Every booking includes private hotel pickup and return transfers. Select your trip below to check availability and request current rates on WhatsApp.
          </p>
        </motion.div>

        {/* 4 Categorized Excursion Groups */}
        <div className="space-y-16">
          {excursionCategories.map((category, catIndex) => <div key={category.id} data-index={catIndex} className="space-y-6">
              {/* Category Header */}
              <div className="border-b border-border pb-4">
                <div className="flex items-center gap-2 mb-1">
                  <span className="size-2 rounded-lg bg-primary" aria-hidden="true" />
                  <h3 className="text-2xl font-bold tracking-tight text-foreground">
                    {category.title}
                  </h3>
                </div>
                <p className="text-sm text-muted-foreground">{category.subtitle}</p>
              </div>

              {/* Excursions Grid for Category */}
              <motion.div variants={containerVariants} initial="hidden" whileInView="show" viewport={{
            once: true,
            amount: 0.1
          }} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {category.items.map((trip, i) => <motion.div key={trip.id} data-index={i} variants={cardVariants} className="flex min-w-0">
                    <Card className="flex flex-col justify-between w-full overflow-hidden border border-border bg-card rounded-md shadow-xs transition-[border-color,box-shadow] duration-200 hover:border-primary/50 hover:shadow-md min-w-0">
                      <div className="min-w-0">
                        {/* Contained Card Image (16:10 ratio) with Badge wrapped in Link */}
                        <Link href={excursionDetailHref(trip.detailSlug)} aria-label={trip.title} className="block">
                          <div className="relative aspect-[16/10] w-full overflow-hidden bg-muted">
                            <img src={trip.image} data-wvc-srcset={trip.srcset} data-wvc-sizes={trip.sizes} alt={trip.alt} loading="lazy" decoding="async" className="h-full w-full object-cover" />

                            {/* Badge Overlay on Image */}
                            <div className="absolute top-3 left-3 flex flex-col gap-1.5 items-start">
                              <span className="inline-flex items-center px-2.5 py-1 rounded-md text-[10px] font-semibold tracking-wider uppercase bg-background/95 text-foreground backdrop-blur-xs border border-border shadow-xs">
                                Private hotel transfer included
                              </span>
                            </div>
                          </div>
                        </Link>

                        <CardContent className="pt-5 pb-4 px-6 flex flex-col gap-3 min-w-0">
                          <Link href={excursionDetailHref(trip.detailSlug)} className="block">
                            <h4 className="text-xl font-semibold tracking-tight text-card-foreground hover:text-primary transition-colors whitespace-normal break-words">
                              {trip.title}
                            </h4>
                          </Link>

                          <p className="text-sm leading-relaxed text-muted-foreground whitespace-normal break-words">
                            {trip.description}
                          </p>
                        </CardContent>
                      </div>

                      {/* Card Footer with Single WhatsApp Quotation Button */}
                      <div className="px-6 pb-6 pt-3 mt-auto border-t border-border flex flex-col gap-3">
                        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
                          <Button asChild variant="outline" size="sm" className="w-full sm:w-1/2 h-9 px-3 rounded-md text-xs font-semibold border-border hover:bg-accent hover:text-accent-foreground transition-colors shadow-xs">
                            <Link href={excursionDetailHref(trip.detailSlug)} aria-label={trip.title}>
                              View Details
                              <ArrowRight className="size-3 ml-1.5 shrink-0" />
                            </Link>
                          </Button>

                          <Button asChild variant="default" size="sm" className="w-full sm:w-1/2 h-9 px-3 rounded-md text-xs font-semibold bg-primary text-primary-foreground hover:bg-primary/90 transition-colors shadow-xs text-center whitespace-normal break-words">
                            <Link to={buildWhatsappLink(trip)} newTab>
                              <MessageSquare className="size-3.5 mr-1 shrink-0" />
                              <span className="truncate">{excursionTerms.en.priceOnWhatsApp}</span>
                            </Link>
                          </Button>
                        </div>
                      </div>
                    </Card>
                  </motion.div>)}
              </motion.div>
            </div>)}
        </div>

        {/* Private Hotel Transfers Information Box */}
        <motion.div initial={{
        opacity: 0,
        y: 14
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        amount: 0.2
      }} transition={{
        duration: 0.45
      }} className="mt-14">
          <ExcursionNotice lang="en" />
        </motion.div>

        {/* Excursion Terms Banner */}
        <motion.div initial={{
        opacity: 0,
        y: 14
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        amount: 0.2
      }} transition={{
        duration: 0.45
      }} className="mt-6">
          <ExcursionTerms lang="en" />
        </motion.div>

        {/* Closing WhatsApp CTA Band */}
        <motion.div initial={{
        opacity: 0,
        y: 14
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        amount: 0.2
      }} transition={{
        duration: 0.45
      }} className="mt-10 flex flex-col items-center justify-center p-8 bg-secondary rounded-md border border-border text-center">
          <h3 className="text-2xl font-bold tracking-tight text-secondary-foreground mb-3">
            Ready to plan your excursion?
          </h3>
          <p className="text-sm md:text-base text-muted-foreground font-normal mb-6 max-w-xl">
            Send us your chosen excursion, hotel name, preferred date, number of adults and children&apos;s ages. We will confirm availability, the programme, included services and your price before booking.
          </p>

          <Button asChild size="lg" className="rounded-md bg-[#27BA2A] text-white font-medium shadow-xs hover:bg-[#22a025] transition-colors px-7 py-3">
            <Link to="https://wa.me/201010634627" newTab>
              <MessageSquare className="size-4 mr-2" />
              Enquire on WhatsApp
            </Link>
          </Button>
        </motion.div>
      </div>
    </section>;
}